import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { getLocationReturnPath, openRecord, useNavigateBack } from '../../hooks/useNavigateBack';
import { cn } from '../../lib/utils';
import { Button, DateInput, Label, Select } from '../../components/ui';
import { fetcher } from '../../services/fetcher';
import { openWrhDay } from '../../services/mutations';
import { moduleConfig } from './config';

const todayIso = () => new Date().toISOString().slice(0, 10);

const Form: React.FC<{ lockedVesselId?: number }> = ({ lockedVesselId }) => {
  const { colors } = useTheme();
  const { showError, showSuccess } = useNotification();
  const { can } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const returnPath = getLocationReturnPath(location.state);
  const goBack = useNavigateBack(moduleConfig.module.route.list, returnPath);
  const canEdit = can(moduleConfig.module.permissionModuleKey, 'edit') || can('vessel_wrh', 'edit');

  const { data: lookups } = useSWR<{ vessels: Array<{ id: number; vesselName: string }> }>(
    '/crew/lookups',
    fetcher,
    { revalidateOnFocus: false },
  );

  const [vesselId, setVesselId] = useState(lockedVesselId ? String(lockedVesselId) : '');
  const [workDate, setWorkDate] = useState(todayIso);
  const [saving, setSaving] = useState(false);

  const vesselOptions = useMemo(
    () => (lookups?.vessels ?? []).map((v) => ({ value: String(v.id), label: v.vesselName })),
    [lookups?.vessels],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canEdit) return;
    const vid = lockedVesselId ?? Number(vesselId);
    if (!vid || !workDate) {
      showError('Vessel and date are required.');
      return;
    }
    setSaving(true);
    try {
      const day = await openWrhDay({ vesselId: vid, workDate });
      showSuccess('WRH day opened.');
      openRecord(
        navigate,
        moduleConfig.module.route.detail(day.id),
        returnPath ?? moduleConfig.module.route.list,
      );
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
      showError(Array.isArray(msg) ? msg.join(' ') : typeof msg === 'string' ? msg : 'Open failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={cn('min-h-full', colors.background)}>
      <div className="mx-auto max-w-xl px-4 py-6 sm:px-6">
        <button
          type="button"
          onClick={goBack}
          className={cn('text-sm font-medium hover:underline', colors.textSecondary)}
        >
          ← Back to WRH
        </button>
        <h1 className={cn('mt-2 text-2xl font-semibold', colors.text)}>Open WRH day</h1>
        <p className={cn('mt-1 text-sm', colors.textSecondary)}>
          Select vessel and date, then generate onboard crew and enter work periods.
        </p>
        <form
          onSubmit={handleSubmit}
          className={cn('mt-6 space-y-4 rounded-xl border p-5 shadow-sm', colors.surface, colors.border)}
        >
          {!lockedVesselId && (
            <div>
              <Label required>Vessel</Label>
              <Select
                value={vesselId}
                onChange={(e) => setVesselId(e.target.value)}
                options={vesselOptions}
                placeholder="Select vessel"
                required
                showClearButton={false}
                disabled={!canEdit}
              />
            </div>
          )}
          <div>
            <Label required>Work date</Label>
            <DateInput value={workDate} onChange={(e) => setWorkDate(e.target.value || todayIso())} disabled={!canEdit} />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={goBack}>
              Cancel
            </Button>
            {canEdit && (
              <Button type="submit" disabled={saving}>
                {saving ? 'Opening…' : 'Open day'}
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default Form;
